/**
 * An Approval step's decision: what the approver is asked to check (the
 * step's instructions, the run's input and the results linked into the step),
 * the decisions made on it so far, and, for a person it names who did not
 * start the run, Approve / Reject / Send back. Shown beside the run chart and
 * in the Inbox; the server decides again who may.
 *
 * @module components/workflows/ApprovalDecision
 */
import type { WorkflowRun } from "@amoreg/contracts";
import { CheckIcon, CornerUpLeftIcon, XIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { refreshInbox } from "../../state/inbox";
import { decideWorkflowApproval } from "../../state/workflows";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { approvalRerunChoices, approvalStanding } from "./workflowCanvas.logic";

type Mode = "idle" | "reject" | "feedback";

const DECISION_LABEL = { approve: "Approved", reject: "Rejected", feedback: "Sent back", done: "Marked done" } as const;

export function ApprovalDecision({
  organizationId,
  run,
  nodeId,
  viewerEmail,
  onDecided,
}: {
  readonly organizationId: string;
  readonly run: WorkflowRun;
  readonly nodeId: string;
  readonly viewerEmail: string | null | undefined;
  readonly onDecided?: (run: WorkflowRun) => void;
}) {
  const node = run.graph.nodes.find((entry) => entry.id === nodeId);
  const step = run.steps.find((entry) => entry.nodeId === nodeId) ?? null;
  const standing = approvalStanding(run, nodeId, viewerEmail);
  const choices = useMemo(() => approvalRerunChoices(run.graph, nodeId), [run.graph, nodeId]);
  const linked = useMemo(() => {
    const outputs = new Map(run.steps.map((entry) => [entry.nodeId, entry.output]));
    return run.graph.edges
      .filter((edge) => edge.target === nodeId)
      .map((edge) => ({
        id: edge.source,
        label: run.graph.nodes.find((entry) => entry.id === edge.source)?.label ?? edge.source,
        output: outputs.get(edge.source) ?? null,
      }));
  }, [run, nodeId]);
  const history = (run.decisions ?? []).filter((entry) => entry.nodeId === nodeId);
  const [mode, setMode] = useState<Mode>("idle");
  const [comment, setComment] = useState("");
  // Sending back defaults to the step just before this one.
  const [rerunFrom, setRerunFrom] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (node?.type !== "approval") return null;
  const target = rerunFrom ?? choices.at(-1)?.id ?? null;

  const decide = async (decision: "approve" | "reject" | "feedback") => {
    if (!step) return;
    setBusy(true);
    setError(null);
    const outcome = await decideWorkflowApproval(organizationId, run.workflowId, run.id, nodeId, {
      attempt: step.attempt,
      decision,
      ...(decision === "approve" || !comment.trim() ? {} : { comment: comment.trim() }),
      ...(decision === "feedback" && target ? { rerunFrom: target } : {}),
    });
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    setMode("idle");
    setComment("");
    // Decided here or in the Inbox: the Inbox drops it either way.
    refreshInbox();
    onDecided?.(outcome.value);
  };

  return (
    <div className="space-y-4" data-workflow-approval={nodeId} data-workflow-approval-standing={standing}>
      {node.config.instructions.trim() ? (
        <div className="space-y-1">
          <p className="text-xs font-medium">What to check</p>
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground">{node.config.instructions}</p>
        </div>
      ) : null}
      {run.input ? (
        <div className="space-y-1">
          <p className="text-xs font-medium">The run's input</p>
          <p className="max-h-32 overflow-auto whitespace-pre-wrap text-[12.5px] leading-relaxed text-muted-foreground">{run.input}</p>
        </div>
      ) : null}
      {linked.map((source) => (
        <div key={source.id} className="space-y-1">
          <p className="text-xs font-medium">{source.label}</p>
          {source.output ? (
            <div
              className="max-h-[40vh] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 text-[12.5px] leading-relaxed text-foreground"
              data-workflow-approval-source={source.id}
            >
              {source.output}
            </div>
          ) : (
            <p className="text-[12.5px] text-muted-foreground" data-workflow-approval-source={source.id}>
              Nothing has reached this step from here yet.
            </p>
          )}
        </div>
      ))}

      {standing === "decide" ? (
        <div className="space-y-2" data-workflow-approval-actions="">
          {mode === "idle" ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => void decide("approve")} disabled={busy} data-workflow-approve="">
                <CheckIcon />
                {busy ? "Approving…" : "Approve"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setMode("feedback")} disabled={busy} data-workflow-send-back="">
                <CornerUpLeftIcon />
                Send back
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setMode("reject")}
                disabled={busy}
                data-workflow-reject=""
              >
                <XIcon />
                Reject
              </Button>
            </div>
          ) : (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium">{mode === "reject" ? "Why are you rejecting it?" : "What should change?"}</span>
                <Textarea
                  autoFocus
                  rows={4}
                  value={comment}
                  maxLength={5_000}
                  onChange={(event) => setComment(event.currentTarget.value)}
                  placeholder={mode === "reject" ? "The person who started the run sees this." : "The steps that run again are given this."}
                  data-workflow-decision-comment=""
                />
              </label>
              {mode === "feedback" ? (
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium">Run again from</span>
                  <Select value={target ?? ""} onValueChange={(value) => setRerunFrom(typeof value === "string" ? value : null)}>
                    <SelectTrigger size="sm" aria-label="Run again from">
                      <span className="truncate text-xs">{choices.find((choice) => choice.id === target)?.label ?? "Choose a step"}</span>
                    </SelectTrigger>
                    <SelectContent>
                      {choices.map((choice) => (
                        <SelectItem key={choice.id} value={choice.id}>
                          {choice.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="block text-[11px] leading-snug text-muted-foreground">
                    That step and every step after it run again, as the person who started the run, then this step waits again.
                  </span>
                </label>
              ) : null}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={mode === "reject" ? "destructive" : "default"}
                  onClick={() => void decide(mode === "reject" ? "reject" : "feedback")}
                  disabled={busy || !comment.trim() || (mode === "feedback" && !target)}
                  data-workflow-decision-confirm=""
                >
                  {busy ? "Sending…" : mode === "reject" ? "Reject the run" : "Send back"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setMode("idle")} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {error ? <p className="text-[13px] text-destructive">{error}</p> : null}
        </div>
      ) : standing === "own-run" ? (
        <p className="text-[13px] text-muted-foreground">You started this run, so one of the other approvers decides it.</p>
      ) : standing === "not-approver" ? (
        <p className="text-[13px] text-muted-foreground">
          Waiting for {node.config.approverEmails.join(", ") || "an approver"}.
        </p>
      ) : null}

      {history.length > 0 ? (
        <div className="space-y-1.5" data-workflow-approval-history="">
          <p className="text-xs font-medium">Decisions</p>
          {history.map((entry) => (
            <div key={`${entry.attempt}-${entry.decidedAt}`} className="rounded-md bg-muted/40 px-2.5 py-1.5 text-[12px]">
              <p>
                <span className="font-medium">{DECISION_LABEL[entry.decision]}</span>
                {entry.byEmail ? ` by ${entry.byEmail}` : ""}
                <span className="text-muted-foreground"> · {formatRelativeTimeLabel(entry.decidedAt)}</span>
                {entry.decision === "feedback" && entry.rerunFrom ? (
                  <span className="text-muted-foreground">
                    {" "}
                    · from {run.graph.nodes.find((candidate) => candidate.id === entry.rerunFrom)?.label ?? entry.rerunFrom}
                  </span>
                ) : null}
              </p>
              {entry.comment ? <p className="whitespace-pre-wrap text-muted-foreground">{entry.comment}</p> : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
